import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { usePageSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";

export default function SeoLanding({ page }) {
  const { lang } = useLanguage();
  const c = page[lang];
  usePageSeo(c.metaTitle, c.metaDescription);

  return (
    <div data-testid={`seo-page-${page.slug}`} className="pt-24">
      <section className="border-b border-line">
        <div className="mx-auto max-w-4xl px-6 pb-20 pt-16 md:pt-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-bronze md:text-sm">
              <span className="h-px w-10 bg-bronze" />
              {c.kicker}
            </p>
            <h1 className="mt-8 font-serif text-4xl leading-[1.02] tracking-tight text-ink md:text-5xl lg:text-6xl">
              {c.h1}
            </h1>
            <p className="mt-8 text-base leading-relaxed text-stone md:text-lg">{c.intro}</p>
          </motion.div>
        </div>
      </section>

      <article className="mx-auto max-w-4xl px-6 py-16 md:py-20">
        {c.sections.map((s, i) => (
          <Reveal key={i}>
            <div className="border-t border-line py-10 first:border-t-0 first:pt-0">
              <h2 className="flex items-baseline gap-4 font-serif text-2xl text-ink md:text-3xl">
                <span className="text-base text-bronze/60">0{i + 1}</span>
                {s.h}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-stone">{s.p}</p>
            </div>
          </Reveal>
        ))}

        <Reveal>
          <Link
            data-testid={`seo-related-${page.slug}`}
            to={page.related.to}
            className="group mt-8 flex items-center justify-between border border-line bg-sand p-7 transition-colors duration-300 hover:border-bronze/50"
          >
            <span className="font-serif text-xl text-ink md:text-2xl">{page.related.label[lang]}</span>
            <ArrowRight className="h-5 w-5 shrink-0 text-bronze transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </article>

      <CTASection />
    </div>
  );
}
